import { useState } from "react"
import styled from "styled-components"

import { Text } from "@minuthq/meatball-ui-react"

import { useDeleteHomeGroup } from "src/data/homeGroups/queries/homeGroupQueries"
import { THomeGroup } from "src/data/homeGroups/types/homeGroupTypes"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { getAccessLogic } from "src/data/user/logic/accessLogic"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { useRouter } from "src/router/useRouter"
import { MButton } from "src/ui/Button/MButton"
import ConfirmDialog from "src/ui/Dialog/ConfirmDialog"
import { Divider } from "src/ui/Divider/Divider"
import { spacing } from "src/ui/spacing"

export function HomeGroupSettings({ homeGroup }: { homeGroup: THomeGroup }) {
  const { t, langKeys } = useTranslate()
  const { navigate } = useRouter()

  const { org } = useOrganization()
  const deleteHomeGroup = useDeleteHomeGroup()
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false)

  function getErrorMessage() {
    if (!deleteHomeGroup.isError) {
      return null
    }

    if (
      deleteHomeGroup.error?.response?.data?.error_key === "homegroup_not_empty"
    ) {
      return t(langKeys.organizations_team_delete_homegroup_error_text)
    }

    return t(langKeys.failed_something_went_wrong)
  }

  function handleDeleteHomeGroupConfirmed() {
    deleteHomeGroup.mutate(
      { orgId: org.id, homeGroupId: homeGroup.id },
      {
        onSuccess: () => setDeleteConfirmOpen(false),
      }
    )
  }

  function handleConvertHomegroup() {
    navigate(
      Routes.ConvertHomeGroup.location({
        name: homeGroup.name,
        homeGroupId: homeGroup.id,
        homeGroupAddress: homeGroup.address
          ? JSON.stringify(homeGroup.address)
          : undefined,
        type: homeGroup.type!,
      })
    )
  }

  const isOrgAdmin = getAccessLogic({ role: org.user_role }).hasAdminAccess
  const editable = isOrgAdmin || homeGroup.user_role === "admin"
  const isBuilding = homeGroup.type === "building"

  return (
    <div>
      <Text variant="heading3" marginBottom={spacing.M}>
        {isBuilding
          ? t(langKeys.home_group_convert_to_location)
          : t(langKeys.home_group_convert_to_building)}
      </Text>
      <Text variant="body" marginBottom={spacing.M}>
        {isBuilding
          ? t(langKeys.home_group_convert_to_location_description)
          : t(langKeys.home_group_convert_to_building_description)}
      </Text>
      <ButtonWrapper>
        <MButton
          variant="subtle"
          disabled={!editable}
          onClick={handleConvertHomegroup}
        >
          {isBuilding
            ? t(langKeys.home_group_convert_to_location)
            : t(langKeys.home_group_convert_to_building)}
        </MButton>
      </ButtonWrapper>
      <Divider $margin={spacing.L} />
      <Text variant="heading3" marginBottom={spacing.M}>
        {isBuilding
          ? t(langKeys.home_group_delete_building)
          : t(langKeys.home_group_delete_location)}
      </Text>

      <Text variant="body" marginBottom={spacing.M}>
        {isBuilding
          ? t(langKeys.home_group_delete_building_description)
          : t(langKeys.home_group_delete_location_description)}
      </Text>
      <ButtonWrapper>
        <MButton
          variant="subtle"
          color="destructive"
          disabled={!editable}
          onClick={() => setDeleteConfirmOpen(true)}
        >
          {isBuilding
            ? t(langKeys.home_group_delete_building)
            : t(langKeys.home_group_delete_location)}
        </MButton>
      </ButtonWrapper>
      <ConfirmDialog
        title={t(langKeys.organizations_delete_team_confirm_title)}
        description={t(langKeys.organizations_home_groups_remove_confirm_body, {
          homegroup: homeGroup.name,
        })}
        loading={deleteHomeGroup.isLoading}
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false)
        }}
        confirmButtonProps={{
          color: "destructive",
        }}
        confirmLabel={t(langKeys.delete)}
        onConfirm={() => handleDeleteHomeGroupConfirmed()}
        error={getErrorMessage()}
      />
    </div>
  )
}

const ButtonWrapper = styled.div`
  margin-top: ${spacing.M};
`
